import React from 'react';

/**
 * LovinLogo — official brand lock-up (PNG renderer).
 *
 * Implementation notes (the painful lessons learned):
 *   • The img element gets EXPLICIT width + height in pixels, not `auto`,
 *     because `width:auto + max-width:100%` is interpreted inconsistently
 *     on iOS Safari and lets the image blow up to fill any flex parent.
 *   • The cropped PNG (see frontend/public/brand/logo.png) has an aspect
 *     ratio of ~5.59:1. We compute width = size * 5.59 and lock both
 *     dimensions on the <img> so flex layout can't stretch it.
 *   • The optional white pill (onDark) uses tight padding so it hugs the
 *     wordmark — just a few px of breathing room — and renders as an
 *     inline-flex span so it never inherits a height from a flex parent.
 *
 * Public API (unchanged for backwards compatibility):
 *   • size       pixel HEIGHT of the wordmark (width is derived)
 *   • onDark     wrap in a white pill for legibility on dark backgrounds
 *   • className  extra wrapper classes
 *   • word/tld/showWordmark/color — accepted but no-op (PNG is a single mark)
 */

// Aspect ratio of the cropped PNG on disk: 4355 × 779 px ≈ 5.59:1.
// If you ever swap the PNG, update this number to match the new image
// (or you'll get distortion).
const LOGO_ASPECT = 4355 / 779;

const LovinLogo = ({
  word = 'Home',
  size = 36,
  onDark = false,
  className = '',
  // unused — kept for API compatibility
  tld: _tld,
  showWordmark: _showWordmark,
  color: _color,
}) => {
  const imgHeight = size;
  const imgWidth = Math.round(size * LOGO_ASPECT);

  // Tight pill: just enough breathing room to read as a chip.
  const padY = Math.max(2, Math.round(size * 0.08));
  const padX = Math.max(8, Math.round(size * 0.22));

  const pillStyle = onDark
    ? {
        background: 'rgba(255,255,255,0.96)',
        padding: `${padY}px ${padX}px`,
        borderRadius: 999,
        boxShadow: '0 4px 18px rgba(0,0,0,0.20)',
        // Hard-cap the pill height so no flex parent can stretch it.
        height: imgHeight + padY * 2,
        lineHeight: 0,
      }
    : { lineHeight: 0 };

  return (
    <span
      className={`inline-flex items-center select-none ${className}`}
      data-testid="lovin-logo"
      aria-label={`Lovin a ${word}.com`}
      style={pillStyle}
    >
      <img
        src="/brand/logo.png"
        alt={`Lovin a ${word}.com`}
        width={imgWidth}
        height={imgHeight}
        style={{
          width: imgWidth,
          height: imgHeight,
          display: 'block',
          // Block flex-parent stretching with absolute caps.
          maxWidth: imgWidth,
          maxHeight: imgHeight,
        }}
        draggable={false}
      />
    </span>
  );
};

export default LovinLogo;
